<template>
  <vuetify-app ref="vuetifyApp"> 
    <pronounce-banner :value="showBanner" :text="bannerText"/>
    <v-col md="12" lg="9" align-self="center" class="mx-auto">
      <v-card class="mx-auto"
              elevation="4"
              max-width="1200">
        <v-container fluid>
          <v-row v-if="$vuetify.breakpoint.mdAndUp">
            <v-col md="6" cols="12" class="br-separator">
              <language-tabs :locale="hl" :language="sl" @current-language="setCurrentLanguage"></language-tabs>
            </v-col>
            <v-col md="6" cols="12">
              <v-tabs full-width class="bb-separator">
                <v-tab> {{ ipaLabel }} </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="pb-0" :class="$vuetify.breakpoint.mdAndUp ? 'br-separator' : 'bb-separator'">
              <input-translate @click="clickWord" @suggested-language="setCurrentLanguage" class="legible" @loading="updateLoading" @stopSpeech="stopSpeech" @ipa="setIpa" :lang-code="sl" :locale='hl'></input-translate>
            </v-col>
            <v-col md="6" cols="12" :class="$vuetify.breakpoint.mdAndUp ? 'gray-bg' : 'pt-6 inverse'">
              <v-container fluid class="px-6 fill-height pb-0">
                <v-row class="fill-height">
                  <p id="ipaContainer" v-on:click="clickIpa" v-html="result" :class="['ipa', 'legible', 'ipaContainer', playing ? 'playing' : '']"></p><loader v-if="showLoader"></loader>
                  <share-btn v-show="plainWords" class='share-btn' v-if="!$vuetify.breakpoint.mdAndUp "/>
                </v-row>
                <v-row >
                  <v-spacer/>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

          <v-row class="tts-controls mt-0 px-2 py-1 mx-auto">
            <text-to-speech-global v-if="$vuetify.breakpoint.mdAndUp">
              <template v-slot:left-prepend>
                <speech-recognition-global @startspeech="startSpeech" @endspeech="endSpeech" @speech="updateInput" ref="recognition"/>
                <word-counter-global/>
                <v-spacer/>
              </template>

              <template v-slot:right>
                <share-btn v-show="plainWords" class='share-btn'/>
              </template>

              <template #voice-select>
                <voice-select-global :open.sync='voiceMenuOpen'/>
              </template>

              <template #speed-slider>
                <speed-slider-popper-global :open.sync='speedPopupOpen'/>
              </template>
            </text-to-speech-global>
          </v-row>
        </v-container>
      </v-card>

      <noscript>
        <strong>We're sorry but <%= htmlWebpackPlugin.options.title %> doesn't work properly without JavaScript enabled. Please enable it to continue.</strong>
      </noscript>
    </v-col>
  </vuetify-app>
</template>

<script>
import Loader from '../components/translate/Loader.vue'
import { ref, watch, computed, provide, onMounted } from 'vue'
import { getCurrentInstance } from 'vue'
import { bindRefToUrlParam, createRefFromUrlParam, getInitialValue } from '../utils/url';
import { inflateTemplate } from '../utils/wordParsing';
import { ttsKeyListeners, useTTS } from '../services/tts';
import { valid2LetterCode } from '../utils/language';
import { parsePlainWords, parsePlainWordsTTS } from '../utils/wordParsing';
import { setupNotifier } from '../components/Notifier.vue';
import i18n from '../plugins/vue-i18n';
import ShareBtn from '../components/ShareBtn.vue';
import { useEvent } from '../plugins/vue-gtag';
import { useSpeech } from '../hooks/useSpeech';
import PronounceBanner from '../components/translate/PronounceBanner.vue'
import { beginTour } from '../utils/tour';

export default {
  components: {
    VuetifyApp: () => import(/* webpackPrefetch: true */
'../components/translate/VuetifyApp.vue'),
    InputTranslate: () => import(/* webpackPrefetch: true */
'../components/translate/InputTranslate.vue'),
    LanguageTabs: () => import(/* webpackPrefetch: true */
'../components/translate/LanguageTabs.vue'),
    WordCounterGlobal: () => import(/* webpackPrefetch: true */
'../components/translate/WordCounterGlobal.vue'),
    TextToSpeechGlobal: () => import(/* webpackPrefetch: true */
'../components/tts/TextToSpeechGlobal.vue'),
    SpeechRecognitionGlobal: () => import(/* webpackPrefetch: true */
'../components/translate/SpeechRecognitionGlobal.vue'),
    VoiceSelectGlobal: () => import(/* webpackPrefetch: true */
'../components/tts/VoiceSelectGlobal.vue'),
    SpeedSliderPopperGlobal: () => import(/* webpackPrefetch: true */
'../components/tts/SpeedSliderPopperGlobal.vue'),
    PronounceBanner,
    Loader,
    ShareBtn
  },
  setup(props, context){
    const root = getCurrentInstance();
    const lastLocaleSelection = localStorage.getItem("last_locale_selection");
    const lastLangSelection = localStorage.getItem("last_language_selection");
    const lastVoiceSelection = localStorage.getItem("last_voice_selection");
    const lastTtsModeSelection = localStorage.getItem("last_tts_mode_selection");
    const lastSpeedSelection = localStorage.getItem("last_speed_selection");
    const initialLocale = getInitialValue('ttsLocale');
    const initialVoice = getInitialValue('voiceId');
    const initialLang = getInitialValue('sl');
    const hl = createRefFromUrlParam('hl', 'en');
    bindRefToUrlParam('hl', hl);
    const ttsLocale = createRefFromUrlParam('ttsLocale');
    bindRefToUrlParam('ttsLocale', ttsLocale);
    const text = createRefFromUrlParam('text', '');
    bindRefToUrlParam('text', text);
    const sl = createRefFromUrlParam('sl', lastLangSelection || 'en');
    bindRefToUrlParam('sl', sl);
    sl.value = (ttsLocale.value && ttsLocale.value.slice(0,2)) || sl.value;
    // if lang code is not valid, fallback to en langauge
    sl.value = valid2LetterCode(sl.value) ? sl.value : (lastLangSelection || 'en');
    const voiceId = createRefFromUrlParam('voiceId');
    bindRefToUrlParam('voiceId', voiceId);
    const ttsMode = createRefFromUrlParam('ttsMode', 'word', (val)=>(val === 'word'));
    bindRefToUrlParam('ttsMode', ttsMode, (val)=>(val ? 'word' : 'sentence'));
    const speed = createRefFromUrlParam('speed', 3, (val)=>(parseInt(val)));
    bindRefToUrlParam('speed', speed);

    const isFirstRequest = lastVoiceSelection && !initialVoice && lastLocaleSelection && !initialLocale && lastLangSelection && !initialLang;
    if(isFirstRequest){
      setTimeout(() => {
        sl.value = lastLangSelection;
        if(lastLocaleSelection.includes(sl.value)){
          ttsLocale.value = lastLocaleSelection;
          voiceId.value = lastVoiceSelection;
          let v = {
            "voiceURI": lastVoiceSelection,
            "name": lastVoiceSelection,
            "lang": lastLocaleSelection
          };
          handleVoiceChanged(v);
        }
        ttsMode.value = lastTtsModeSelection === 'false' ? false : true;
        speed.value = parseInt(lastSpeedSelection, 10);
      },1000);
    }

    i18n.locale = hl.value;


    const core = ref(null);
    const subtitleOpen = ref(false);
    const appInvalidInput = ref(false);
    const showLoader = ref(false);
    const ipaText = ref(null);
    const isTyping = ref(false);
    const useIpa = ref(false);
    const showBanner = ref(false);
    const bannerText = ref(null);
    const showImage = ref(false);
    const imageSrc = ref(null);

    const plainWords = computed(()=>{
      if (!text.value) return null;
      return parsePlainWords(text.value, ttsMode.value); 
    })

    let isPronouncing = false;
    let pronounceQueue = [];
    const voiceMenuOpen = ref(false);
    const speedPopupOpen = ref(false);

    const plainWordTTS = computed(()=>{
      if (!text.value) return null;
      return parsePlainWordsTTS(text.value, ttsMode.value); 
    })

    const plainSymbols = computed(()=>{
      if (!ipaText.value) return null;
      return parsePlainWords(ipaText.value, ttsMode.value); 
    })

    const { 
      snackbarMessage
    } = setupNotifier();

    const {
      ttsIndex,
      playing,
      handleVoiceChanged,
      ttsManagerService,
    } = useTTS({
      locale: ttsLocale,
      textArray: plainWordTTS,
      ipaTexts: plainSymbols,
      language: sl,
      speed,
      voiceId,
      useIpa,
      ttsMode
    });

    const inflateTemplateFn = (markIndex, textId, speed, onlySentence) => {
      const audioMap = textId && core?.value?.audiosMaps ? core?.value?.audiosMaps[textId] : core?.value?.currentAudioMap;
      const allHtml = inflateTemplate(text.value, markIndex, ttsMode.value, ipaText.value, core?.value?.currentTime?.value, audioMap, speed);
      if(onlySentence){
        const query = `[data-index="${markIndex}"]`;
        const doc = new DOMParser().parseFromString(`<div>${allHtml}<div>`, "text/xml");
        const element = doc.querySelectorAll(query);
        return '&nbsp;' + element?.[0]?.innerHTML.replace(/\<\/span\>/g, '</span> &nbsp;',);
      }
      return allHtml;
    };

    const highlightedIpa = ref('');
    const highlightedIpa2 = ref(inflateTemplateFn());
    
    onMounted(async () => {
      setTimeout(async () => {
        core.value = await ttsManagerService.value.getCore();
        core.value.addEventListener('timeupdate', () => {
          const word = plainWords.value[ttsIndex.value];
          const textId = word.trim() + speed.value + voiceId.value; // + (useIpa.value ? 'ipa' : '');
          highlightedIpa2.value = inflateTemplateFn(ttsIndex.value, textId, speed.value, showBanner.value);
        },20);
      });
    });
    ttsManagerService.value.addEventListener('error',(err)=>{
      console.error('error')
      snackbarMessage.value = err.message;
    });

    ttsKeyListeners(isTyping);

    const updateLoading = (loading) => {
      showLoader.value = loading;
    };

    const setCurrentLanguage = async (langCode) => {
      console.log(langCode);
      localStorage.setItem("last_language_selection", langCode);
      sl.value = langCode;
      ttsLocale.value = langCode;
      setTimeout(() => {
        window.location.reload();
      }, 200);

      if (core?.value) {
        core.value.destroy();  // Cleanup event listeners
      }
      setTimeout(async () => {
        core.value = await ttsManagerService.value.getCore();
        core.value.addEventListener('timeupdate', () => {
          const word = plainWords.value[ttsIndex.value];
          const textId = word.trim() + speed.value + voiceId.value; // + (useIpa.value ? 'ipa' : '');
          highlightedIpa2.value = inflateTemplateFn(ttsIndex.value, textId, speed.value, showBanner.value);
        },20);
      });
    };

    const setIpa = (ipa) => {
      ipaText.value = ipa;
      let re = inflateTemplateFn();
      highlightedIpa2.value = re;
      return re;
    };


    const result = computed(()  => {
      return highlightedIpa.value && highlightedIpa.value.length > 0 && highlightedIpa.value || placeholder.value
    });
    const placeholder = computed(() => {
      return `<span class="ipa-placeholder">${i18n.t('pages.ipa_translate.translation')}</span>`
    });
    const ipaLabel = computed(() => {
      if(i18n.locale){
        return i18n.t('pages.ipa_translate.ipa') + ` (${ttsLocale.value})`;
      }
      return i18n.t('pages.ipa_translate.ipa');
    });

    const unmarkIndex = (index, expected)=>{
      function unmarkCallback(pronounced){
        let inputContainer = document.getElementById('source-input');
        let ipaContainer = document.getElementById('ipaContainer');
        let el = inputContainer.getElementsByClassName('pronounceable')[index];
        let el2 = ipaContainer.getElementsByClassName('pronounceable')[index];
        if (expected === pronounced){
          el.classList.remove('mark');
          el2.classList.remove('mark');

          showBanner.value = false;
        }
        ttsManagerService.value.removeEventListener('pronounced', unmarkCallback);
        if (pronounceQueue.length > 0){
          const nextPronounciation = pronounceQueue.shift();
          nextPronounciation();
        } else {
          isPronouncing = false;
        }
      }
      ttsManagerService.value.addEventListener('pronounced', unmarkCallback);
    }

    const displayBanner = (text)=>{
      showBanner.value = true;
      isPronouncing = true;
      bannerText.value = text;
    }

    const highlightWord = (wordIndex)=>{
      let inputContainer = document.getElementById('source-input');
      let ipaContainer = document.getElementById('ipaContainer');
      let el = inputContainer.getElementsByClassName('pronounceable')[wordIndex];
      let el2 = ipaContainer.getElementsByClassName('pronounceable')[wordIndex];
      el.classList.add('mark');
      el2.classList.add('mark');
    }

    const gaForwardCursorTTS = useEvent('forward_tts'); 
    const gaRewindCursorTTS = useEvent('rewind_tts');
    const gaPronounceTTS = useEvent('pronounce_tts');

    const handleIndexChange = (newTtsIndex)=>{
      if (newTtsIndex > ttsIndex.value) {
        gaForwardCursorTTS(newTtsIndex);
      } else {
        gaRewindCursorTTS(newTtsIndex);
      }
      ttsIndex.value = newTtsIndex;
    }

    const handlePronounceWord = (newTtsIndex, isIpa)=>{
      const word = plainWords.value[newTtsIndex];
      const ipa = plainSymbols.value[newTtsIndex];
      const pronounceCallback = ()=>{
        displayBanner(ipa);
        ttsManagerService.value.pronounce(word, {
          ipa: isIpa ? ipa : null,
          callback: () => {
            useIpa.value = false;
          }
        })
        unmarkIndex(newTtsIndex, word);
        gaPronounceTTS(word);
      }
      if (!isPronouncing){
        pronounceCallback();
      } else {
        pronounceQueue.push(pronounceCallback);
      }
    }

    const clickIpa = (e, isIpa = true)=>{
      if(!playing.value){
        useIpa.value = isIpa;
      } else {
        useIpa.value = false;
      }
      let i = parseInt(e.target.dataset.index,10);

      if(e.target.matches('.clickable')){
        if (playing.value){
          handleIndexChange(i);
        } else { 
          highlightWord(i);
          handleIndexChange(i);
          handlePronounceWord(i, isIpa);
        }
      }
    }

    const clickWord = (e)=>{
      return clickIpa(e, false)
    }

    const handleTTSMode = (newTTSModeValue)=>{
      localStorage.setItem("last_tts_mode_selection", newTTSModeValue);
      ttsMode.value = newTTSModeValue;
    }
    
    const highlight = (trackingIndex) => {
      if(!playing.value){
        highlightedIpa2.value = inflateTemplateFn();
        return;
      }
      const word = plainWords.value[trackingIndex];
      const textId = word.trim() + speed.value + voiceId.value; // + (useIpa.value ? 'ipa' : '');
      const html = inflateTemplateFn(trackingIndex, textId, speed.value, showBanner.value);
      if(showBanner.value) {
        bannerText.value = html;
      } else {
        if(!plainSymbols.value){
          return null;
        }
        if(!playing.value){
          highlightedIpa2.value = inflateTemplateFn();
        }
        highlightedIpa2.value = html;
      }
    }

    watch(hl, (newValue)=>{
      i18n.locale = newValue;
    })
    watch(playing, (newPlaying) => {
      if(newPlaying){
        highlight(0);
      } else {
        highlight(ttsIndex.value);
      }
    });
    watch(ttsIndex, (trackingIndex) => {
      highlight(trackingIndex);
    });
    watch(highlightedIpa2, (newVal) => {
      highlightedIpa.value = newVal;
    });
    ttsManagerService.value.addEventListener('started',()=>{
      const steps = [
        {
          class: 'subtitle-btn',
          description: root.proxy.$i18n.t('tour.subtitles'),
          position: 'bottom',
          nextAction: (next)=>{
            subtitleOpen.value = false;
            next();
          }
        },
        {
          class: 'tts-play-pause-btn',
          description: root.proxy.$i18n.t('tour.play-pause'),
          position: 'top'
        },
        {
          class: 'tts-mode-btn',
          description: root.proxy.$i18n.t('tour.tts-mode'),
          position: 'top'
        },
        {
          class: 'tts-voice-selector',
          description: root.proxy.$i18n.t('tour.voice-selector'),
          nextAction: (next)=>{
            voiceMenuOpen.value = false;
            next();
          },
          beforeShowPromise: async ()=>{
            voiceMenuOpen.value = true;
          }
        },
        {
          class: 'speed-slider-btn',
          description: root.proxy.$i18n.t('tour.tts-speed'),
          nextAction: (next)=>{
            speedPopupOpen.value = false;
            next();
          },
          beforeShowPromise: async ()=>{
            speedPopupOpen.value = true;
          }
        }
      ]

      const tourBegun = beginTour(steps);
      if (tourBegun){
        setTimeout(()=>{
          ttsManagerService.value.pause();          
        }, 200)
      }
    })

    provide('language', sl);
    provide('appTTS', text);
    provide('ipaText', ipaText);
    provide('plainWords', plainWords);
    provide('plainSymbols', plainSymbols);
    provide('isTyping', isTyping);
    provide('ttsMode', ttsMode);
    provide('appInvalidInput', appInvalidInput);
    provide('handleTTSMode', handleTTSMode);
    provide('hl', hl);
    provide('subtitleOpen', subtitleOpen)
    provide('speedPopupOpen', speedPopupOpen)
    provide('voiceMenuOpen', voiceMenuOpen)
    provide('core', core);
    provide('speed', speed);
    provide('voiceId', voiceId);
    provide('useIpa', useIpa);
    provide('showImage', showImage);
    provide('imageSrc', imageSrc);
    provide('text', text);

    const {
      updateInput,
      stopSpeech,
      startSpeech,
      endSpeech
    } = useSpeech(text, root);

    return {
      showLoader,
      sl,
      ipaLabel,
      ipaText,
      plainWords,
      plainSymbols,
      setIpa,
      result,
      setCurrentLanguage,
      updateLoading,
      clickIpa,
      clickWord,
      hl,
      showBanner,
      bannerText,
      voiceMenuOpen,
      speedPopupOpen,
      playing,
      updateInput,
      stopSpeech,
      startSpeech,
      endSpeech
    };
  },
  metaInfo() {
    return {
      title: this.$i18n.t('pages.ipa_translate.title'),
    }
  }
}
</script>

<style lang="scss">
  .fill-height {
    line-height: 1.4;
  }
  .br-separator {
    border-right: 1px solid #ccc;
  }
  .bb-separator > .v-tabs-bar, .col-md-6.bb-separator {
    border-bottom: 1px solid #ccc;
  }
  .bt-separator {
    border-top: 1px solid #ccc;
  }
  .legible div, .legible textarea, p.legible {
    font-size: 20px;
  }
  p.ipa {
    padding: 12px;
    padding-top: 0;
    margin-top: -12px;
  }
  .nested-mark {
    background: #6D8E67 !important;
    color: white !important;
  }
  .v-application {
    &.mobile {
      .share-btn {
        position: absolute;
        bottom: 10px;
        right: 20px;
      }
    }
    &.theme--dark{
      .source-input {
      }

      textarea,
      .ipaContainer span{
        color: var(--dark-input-text);
      }

      textarea::placeholder,
      .suggested-language *,
      .ipaContainer .ipa-placeholder {
      }

      p.ipa .even:not(.mark) {
      }
      .ipaContainer.ipa.playing {
        color: var(--dark-input-text);
      }

      &.mobile{
      }
      .inverse {

        *,
        .ipaContainer span,
        svg.iconify path{
          color: var(--dark-input-text);
        }
      }

      .ipaContainer span.mark{
        background-color: var(--dark-highlight);
        color: var(--light-highlight-text);
        text-shadow: unset;
      }
    }
    &.theme--light{
      .source-input {
      }
      .ipaContainer {
        width: 100%;
      }

      .ipaContainer span,
      textarea{
        color: var(--light-readonly-text);
      }
      .ipaContainer .even {
      }

      textarea::placeholder,
      .suggested-language *,
      .ipaContainer .ipa-placeholder {
      }

      .inverse {
        
        *,
        .ipaContainer span.mark {
          color: var(--light-highlight-text);
        }
      }
    }

    .source-input span.mark, .source-input span.mark span,
    .ipaContainer span.mark{
      background-color: var(--light-highlight);
      color: var(--light-highlight-text);
      text-shadow: unset;
    }
  }
  span.font-size-smaller {
    font-size: smaller;
  }
  .h-auto.v-btn.v-btn--text.v-size--default.primary--text {
    height: auto;
  }
  .correct-spelling {
    display: block;
    white-space: pre-line;
    width: fit-content;
    margin-top: auto;
  }
  .clickable {
    cursor: pointer;
  }
  .v-tabs .v-item-group.v-tabs-bar {
    transition:none;
  }
  .tts-controls .row {
    justify-content: center;
  }
  .ipaContainer {
    word-break: keep-all;
    * {
      font: caption;
      font-size: 20px;
    }
    .non-symbol, .non-symbol span {
      font-size: 0.75rem;
      margin: 0px 2px;
      color: #888888;
    }
    pre {
      display: inline-block;
      word-spacing: 0;
    }
  }


  .ipaContainer.ipa.playing .even {
    filter: initial;
    text-shadow: unset;
  }

</style>
